import React, { useMemo, useState } from 'react';
import GlobalSupervisionTab from '../components/admin/GlobalSupervisionTab';
import EstablishmentsTab from '../components/admin/EstablishmentsTab';
import AdminLogin from '../components/admin/AdminLogin';
import CreateLicenseModal from '../components/admin/CreateLicenseModal';
import AdminHeader from '../components/admin/adminTabs/AdminHeader';
import DashboardTab from '../components/admin/adminTabs/DashboardTab';
import LicensesTab from '../components/admin/adminTabs/LicensesTab';
import CohortsTab from '../components/admin/adminTabs/CohortsTab';
import NotificationsTab from '../components/admin/adminTabs/NotificationsTab';
import LeadsTab from '../components/admin/adminTabs/LeadsTab';
import RexTab from '../components/admin/adminTabs/RexTab';
import useAdminPanelData from '../components/admin/adminTabs/useAdminPanelData';
import useLicenseActions from '../components/admin/adminTabs/useLicenseActions';
import {
  DEFAULT_LICENSE_FILTERS, DEFAULT_LICENSE_SORT, filterLicenses, sortLicenses, countByStatus,
} from '../utils/adminLicenseFilters';

/**
 * AdminPanel — thin orchestrator (refactored 29/05/2026 from 738 lines).
 *
 * Tabs are rendered as standalone components living in
 * `components/admin/adminTabs/`. Data is fetched in `useAdminPanelData`,
 * and CRUD actions live in `useLicenseActions`. All data-testids and
 * behaviour are preserved verbatim.
 */
export default function AdminPanel() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState('dashboard');
  const [showCreate, setShowCreate] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all'); // 'all' | 'master' | 'user'
  // 02/06/2026 — Allow admin to hide TEST_* licenses created by automated
  // test suites so the real cohort list isn't polluted.
  // 03/06/2026 — Toggle promoted to ALL admin tabs (was Licenses-only). The
  // filter is now applied to a single `visibleLicenses` derived array that
  // feeds every downstream view (cohorts, establishments, leads, notifs).
  const [hideTest, setHideTest] = useState(true);
  // 21/09/2026 — Filtres Licences (statut / dates) + tri, onglet Licences uniquement.
  const [licFilters, setLicFilters] = useState(DEFAULT_LICENSE_FILTERS);
  const [licSort, setLicSort] = useState(DEFAULT_LICENSE_SORT);

  const { licenses, stats, notifLog, loading, fetchLicenses, fetchStats, fetchNotifLog } = useAdminPanelData(admin);
  const actions = useLicenseActions({ admin, fetchLicenses, fetchStats, fetchNotifLog });

  const isTestLicense = (l) => {
    const n = (l.holder_name || '').toUpperCase();
    const e = (l.holder_email || '').toUpperCase();
    return n.startsWith('TEST_') || n.startsWith('TEST ') || e.startsWith('TEST_') || e.includes('@TEST.');
  };

  // Global filtered list, used by ALL admin tabs.
  const visibleLicenses = useMemo(
    () => (hideTest ? licenses.filter((l) => !isTestLicense(l)) : licenses),
    [licenses, hideTest],
  );

  const searched = useMemo(() => visibleLicenses.filter((l) => {
    const q = search.toLowerCase();
    const matchesSearch = !search
      || l.holder_name.toLowerCase().includes(q)
      || l.license_key.toLowerCase().includes(q)
      || (l.holder_email || '').toLowerCase().includes(q)
      || (l.owner || '').toLowerCase().includes(q);
    if (!matchesSearch) return false;
    if (typeFilter === 'master') return !!l.is_master;
    if (typeFilter === 'user') return !l.is_master;
    return true;
  }), [visibleLicenses, search, typeFilter]);
  // Compteurs de statut calculés AVANT le filtre statut (pour les chips).
  const statusCounts = useMemo(() => countByStatus(searched), [searched]);
  const filtered = useMemo(
    () => sortLicenses(filterLicenses(searched, licFilters), licSort),
    [searched, licFilters, licSort],
  );

  const testCount = useMemo(() => licenses.filter(isTestLicense).length, [licenses]);

  const masterLicenses = useMemo(() => visibleLicenses.filter((l) => l.is_master), [visibleLicenses]);
  const cohorts = useMemo(() => masterLicenses.map((m) => ({
    master: m,
    children: visibleLicenses.filter((l) => !l.is_master && l.master_license_id === m.id),
  })), [masterLicenses, visibleLicenses]);
  const orphanApprentices = useMemo(
    () => visibleLicenses.filter((l) => !l.is_master && !l.master_license_id),
    [visibleLicenses],
  );
  // 18/09/2026 — Onglet REX : libellés de cohorte résolus sur TOUTES les
  // licences (un REX rattaché à un master masqué par « Cacher TEST » doit
  // quand même afficher le nom de sa cohorte).
  const rexCohorts = useMemo(() => licenses.filter((l) => l.is_master).map((m) => ({
    master: m,
    children: licenses.filter((l) => !l.is_master && l.master_license_id === m.id),
  })), [licenses]);

  if (!admin) return <AdminLogin onLogin={setAdmin} />;

  const refreshAll = () => { fetchLicenses(); fetchStats(); };

  return (
    <div className="min-h-screen bg-ink-700/30" data-testid="admin-panel">
      <AdminHeader
        adminEmail={admin.email}
        tab={tab}
        setTab={setTab}
        onLogout={() => setAdmin(null)}
        hideTest={hideTest}
        setHideTest={setHideTest}
        testCount={testCount}
      />

      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {tab === 'dashboard' && <DashboardTab stats={stats} />}

        {tab === 'licenses' && (
          <LicensesTab
            filtered={filtered}
            licenses={licenses}
            masterLicenses={masterLicenses}
            loading={loading}
            search={search} setSearch={setSearch}
            typeFilter={typeFilter} setTypeFilter={setTypeFilter}
            hideTest={hideTest} setHideTest={setHideTest} testCount={testCount}
            licFilters={licFilters} setLicFilters={setLicFilters}
            licSort={licSort} setLicSort={setLicSort}
            statusCounts={statusCounts}
            copied={actions.copied}
            resendingId={actions.resendingId}
            token={admin.token}
            onRefresh={refreshAll}
            onCreate={() => setShowCreate(true)}
            onCopyKey={actions.copyKey}
            onToggleLicense={actions.toggleLicense}
            onDeleteLicense={actions.deleteLicense}
            onResendWelcome={actions.resendWelcome}
            onLicensesChanged={fetchLicenses}
          />
        )}

        {tab === 'cohorts' && (
          <CohortsTab
            cohorts={cohorts}
            masterLicenses={masterLicenses}
            orphanApprentices={orphanApprentices}
            bulkResendingId={actions.bulkResendingId}
            token={admin.token}
            onCreate={() => setShowCreate(true)}
            onBulkResend={actions.bulkResendCohort}
            onLicensesChanged={fetchLicenses}
          />
        )}

        {tab === 'establishments' && (
          <EstablishmentsTab licenses={visibleLicenses} token={admin.token} onChanged={fetchLicenses} />
        )}

        {tab === 'leads' && <LeadsTab token={admin.token} hideTest={hideTest} />}

        {tab === 'rex' && <RexTab token={admin.token} cohorts={rexCohorts} />}

        {tab === 'supervision' && <GlobalSupervisionTab token={admin.token} hideTest={hideTest} />}

        {tab === 'notifications' && (
          <NotificationsTab
            licenses={visibleLicenses}
            notifLog={notifLog}
            checkingNotifs={actions.checkingNotifs}
            onTriggerCheck={actions.triggerNotifCheck}
          />
        )}
      </div>

      <CreateLicenseModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={refreshAll}
        token={admin.token}
        masterLicenses={masterLicenses}
      />
    </div>
  );
}
